import { z } from 'zod';
import { Api } from '@axiom/ui';
import { SchemaEmail, SchemaPassword } from '@axiom/types';
import { MfaVerificationSchema } from '@axiom/validation';

import { EnvUtil } from '../utils/env-util';

import type { ReadAuthResponse } from './generated/dtos/read-auth-response';
import type { UpdateAuthResetResponse } from './generated/dtos/update-auth-reset-response';

export const readAuthRequestSchema = z.object({ email: SchemaEmail });
export type ReadAuthRequest = Required<z.infer<typeof readAuthRequestSchema>>;

export const createAuthPasswordRequestSchema = z
  .object({
    email: SchemaEmail,
    password: SchemaPassword,
  })
  .merge(MfaVerificationSchema);
export const createAuthTokenRequestSchema = z.object({
  token: z.string(),
});
export type CreateAuthPasswordRequest = Required<
  z.infer<typeof createAuthPasswordRequestSchema>
>;
export type CreateAuthTokenRequest = Required<
  z.infer<typeof createAuthTokenRequestSchema>
>;
export type CreateAuthResponse = string;

class AuthApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.envoyApiUrl,
    });
  }

  async createAuth(
    body:
      | CreateAuthPasswordRequest
      | CreateAuthTokenRequest
      | { mfaCode: string | null }
  ) {
    return super.write<{ data: CreateAuthResponse }>({
      endpoint: `/auth`,
      method: 'POST',
      body,
    });
  }

  readAuthForEmail({
    email,
    query = {},
  }: {
    email: string;
    query?: { RelayState?: string };
  }) {
    return super.read<{ data: ReadAuthResponse }>({
      endpoint: `/auth/${email}`,
      method: 'GET',
      query,
    });
  }

  async updateTokenLogin({ token }: { token: string }) {
    return super.write<UpdateAuthResetResponse>({
      endpoint: `/auth/tokenLogin/${token}`,
      method: 'PATCH',
    });
  }
}

export const AuthApi = new AuthApiClass();
